<template>
  <EChartCard
    :title="chartTitle"
    :buildOption="buildOption"
    :height="380"
    @chart-click="handleChartClick"
  />
</template>

<script setup>
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { getSequential, statusColors } from '@/utils/vizPalette';
import { axisColor, DIM_OPACITY } from '@/utils/chartTheme';
import { useSelectableFilter } from '@/composables/useSelectableFilter';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();

const chartName = 'ratingChart';
const chartTitle = t('ratingDistribution');

// Bucket for assets that carry no rating (never inspected).
const NOT_INSPECTED = 'Not Inspected';

// Fixed, ordinal category order — best to worst, then the "no rating" bucket.
const RATING_ORDER = ['Excellent', 'Good', 'Satisfactory', 'Fair', 'Poor', NOT_INSPECTED];

const { isSelected, hasSelection, sync, toggle } = useSelectableFilter(chartName);

const categoryLabel = (rating) => (rating === NOT_INSPECTED ? t('notInspected') : rating);

// Sequential single hue, darkest = Excellent, lightest = Poor; the un-rated
// bucket is deliberately outside the ramp and uses the reserved neutral.
const ratingColors = (theme) => {
  const ramp = [...getSequential(theme)].reverse();
  const colors = {};
  RATING_ORDER.forEach((rating, index) => {
    colors[rating] = rating === NOT_INSPECTED
      ? statusColors.neutral
      : ramp[Math.min(index, ramp.length - 1)];
  });
  return colors;
};

const getRatingCounts = async () => {
  const conn = store.getters.getDBConnection;
  if (!conn) return {};

  // Unique assets, rating rolled up from the latest inspection that recorded one.
  const result = await conn.query(`
    SELECT COALESCE(rating, ${sqlLiteral(NOT_INSPECTED)}) AS r, COUNT(*) AS c
    FROM filteredAssets
    GROUP BY r
  `);
  const counts = {};
  result.toArray().forEach((row) => {
    const jsonRow = row.toJSON();
    counts[jsonRow.r] = Number(jsonRow.c) || 0;
  });
  return counts;
};

const buildOption = async () => {
  sync();
  const counts = await getRatingCounts();

  const theme = store.state.theme;
  const fontColor = store.getters.chartFontColor;
  const colors = ratingColors(theme);
  const gridColor = axisColor(theme);

  // Zero-fill so the axis always shows the full ordinal scale.
  const values = RATING_ORDER.map((rating) => counts[rating] || 0);
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total === 0) return null;

  return {
    animationDuration: 600,
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params) => {
        const value = params.value || 0;
        const share = total > 0 ? Math.round((value / total) * 100) : 0;
        return `<strong>${params.name}</strong><br/>${value} (${share}%)`;
      }
    },
    grid: { left: '3%', right: '4%', top: 24, bottom: 4, containLabel: true },
    xAxis: {
      type: 'category',
      data: RATING_ORDER.map(categoryLabel),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: gridColor } },
      axisLabel: { color: fontColor, fontSize: 12, interval: 0, overflow: 'truncate', width: 90 }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: fontColor },
      splitLine: { lineStyle: { color: gridColor } }
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 48,
        data: RATING_ORDER.map((rating, index) => ({
          value: values[index],
          itemStyle: {
            color: colors[rating],
            borderRadius: [4, 4, 0, 0],
            opacity: hasSelection.value && !isSelected(rating) ? DIM_OPACITY : 1
          }
        })),
        label: {
          show: true,
          position: 'top',
          color: fontColor,
          fontSize: 12,
          formatter: (params) => (params.value > 0 ? params.value : '')
        }
      }
    ]
  };
};

const handleChartClick = async (params) => {
  if (params.componentType !== 'series' || params.dataIndex === undefined) return;

  const rating = RATING_ORDER[params.dataIndex];
  if (!rating) return;

  const condition = rating === NOT_INSPECTED ? 'rating IS NULL' : `rating = ${sqlLiteral(rating)}`;
  await toggle(rating, condition, `${t('rating')}: ${categoryLabel(rating)}`);
};
</script>
